/**
 * The `radio` and `select` tools' spec, as the picker reads it: a `question`, a list of
 * `options`, and a `multiple` flag. The tools themselves are defined and executed by
 * sphere-mcp (src/server/chat/ui-tools.ts, CROSS-REPO CONTRACT kept in sync by hand); this
 * is the reading side, for SphereAIChoice.
 */
import { z } from "zod";

export const RADIO_TOOL_NAME = "radio";
export const SELECT_TOOL_NAME = "select";

/** True for the client-side choice tools (radio / select). */
export function isChoiceToolName(name: string): name is typeof RADIO_TOOL_NAME | typeof SELECT_TOOL_NAME {
	return name === RADIO_TOOL_NAME || name === SELECT_TOOL_NAME;
}

const choiceOptionSchema = z.object({
	/** Human-readable text shown to the user. */
	label: z.string().min(1),
	/**
	 * Machine value returned to the model as the tool result when this option is picked.
	 * Optional — defaults to `label` (so a plain list of options "just works").
	 */
	value: z.string().min(1).optional(),
	/** Optional secondary line shown under the label (radio only). */
	description: z.string().optional(),
});

const choiceSpecSchema = z.object({
	/** The question put to the user, shown above the options. */
	question: z.string().min(1),
	options: z.array(choiceOptionSchema).min(1),
	/** Allow picking several options (default false). When true the user confirms with a button. */
	multiple: z.boolean().optional(),
});

/** A choice option after normalization — `value` is guaranteed present. */
export type ChoiceOption = { label: string; value: string; description?: string };
export type ChoiceSpec = { question: string; options: ChoiceOption[]; multiple: boolean };

/**
 * Validate a tool-call input as a choice spec. Returns the parsed spec (with each option's
 * `value` defaulted to its `label`) or a human-readable error.
 */
export function parseChoiceSpec(input: unknown): { ok: true; spec: ChoiceSpec } | { ok: false; error: string } {
	const parsed = choiceSpecSchema.safeParse(input);
	if (!parsed.success) {
		const error = parsed.error.issues.map((i) => `${i.path.join(".") || "(root)"}: ${i.message}`).join("; ");
		return { ok: false, error };
	}
	const spec: ChoiceSpec = {
		question: parsed.data.question,
		options: parsed.data.options.map((o) => ({
			label: o.label,
			value: o.value ?? o.label,
			description: o.description,
		})),
		multiple: parsed.data.multiple ?? false,
	};
	return { ok: true, spec };
}
